import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ThumbsUp, MessageSquareWarning, ExternalLink, X, ClipboardCheck } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { demandaDoCliente } from '@/lib/distribuicao';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';
import type { Demanda, EntregaEscopo } from '@/lib/types';

export function AprovarPortal() {
  const { user } = useAuth();
  const portalSid = usePortalSid();
  const sid = portalSid ?? user?.solicitanteId ?? '';
  const qc = useQueryClient();
  const demandasQ = useQuery({ queryKey: ['portal', 'demandas'], queryFn: () => api.demandas.list().then(r => r.data) });
  const escopoQ = useQuery({ queryKey: ['portal', 'escopo'], queryFn: () => api.escopoEntregas.list().then(r => r.data) });

  const aguardando = useMemo(() =>
    (demandasQ.data ?? []).filter(d => demandaDoCliente(d, sid) && d.status === 'Aguardando aprovação'),
    [demandasQ.data, sid]);
  const escopoAguardando = useMemo(() =>
    (escopoQ.data ?? []).filter(e => e.status === 'Aguardando aprovação'),
    [escopoQ.data]);

  const aprovar = useMutation({
    mutationFn: ({ id, comentario }: { id: string; comentario?: string }) => api.demandas.aprovar(id, comentario),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'demandas'] }),
  });
  const revisao = useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) => api.demandas.pedirRevisao(id, feedback),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'demandas'] }),
  });
  const aprovarEscopo = useMutation({
    mutationFn: (id: string) => api.escopoEntregas.aprovar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'escopo'] }),
  });
  const revisaoEscopo = useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) => api.escopoEntregas.pedirRevisao(id, feedback),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'escopo'] }),
  });

  const [revOpen, setRevOpen] = useState<Demanda | null>(null);
  const [revEscopoOpen, setRevEscopoOpen] = useState<EntregaEscopo | null>(null);
  const [feedback, setFeedback] = useState('');

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-text">Pra aprovar</h1>
        <p className="text-sm text-text-2">
          {aguardando.length + escopoAguardando.length === 0 ? 'Nenhuma peça pendente — você está em dia ✓' :
           `${aguardando.length + escopoAguardando.length} ${(aguardando.length + escopoAguardando.length) === 1 ? 'item aguardando' : 'itens aguardando'} sua revisão`}
        </p>
      </header>

      {aguardando.length + escopoAguardando.length === 0 && (
        <div className="bg-green/10 border border-green/30 rounded-xl p-8 text-center">
          <ThumbsUp className="w-10 h-10 mx-auto text-green mb-2" />
          <p className="text-sm text-green-700 font-semibold">Tudo aprovado! Volte mais tarde.</p>
        </div>
      )}

      {/* Escopo items for approval */}
      {escopoAguardando.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-text uppercase tracking-wider mb-2 flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5 text-purple-500" />
            Entregas do escopo
          </h3>
          <div className="space-y-3">
            {escopoAguardando.map(e => (
              <div key={e.id} className="bg-panel border border-purple/30 bg-purple/5 rounded-xl p-4">
                <h3 className="font-bold text-text">{e.entrega}</h3>
                <p className="text-xs text-text-2 mt-1">
                  {e.eventoSubtipo && <span className="font-medium">{e.eventoSubtipo}</span>}
                  {e.eventoSubtipo && ' · '}{e.fase}
                  {e.quantidade > 1 && ` · ×${e.quantidade}`}
                </p>
                {e.observacoes && <p className="text-xs text-text-2 mt-1 italic">{e.observacoes}</p>}
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => aprovarEscopo.mutate(e.id)}
                    disabled={aprovarEscopo.isPending}
                    className="flex-1 bg-green hover:bg-green/90 text-white text-xs font-bold py-2 rounded-md inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <ThumbsUp className="w-3.5 h-3.5" /> Aprovar
                  </button>
                  <button type="button" onClick={() => { setRevEscopoOpen(e); setFeedback(''); }}
                    className="flex-1 bg-purple/10 border border-purple/30 text-purple text-xs font-bold py-2 rounded-md inline-flex items-center justify-center gap-1.5 hover:bg-purple/20">
                    <MessageSquareWarning className="w-3.5 h-3.5" /> Pedir ajustes
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demandas for approval */}
      {aguardando.length > 0 && escopoAguardando.length > 0 && (
        <h3 className="text-xs font-bold text-text uppercase tracking-wider flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5 text-red-500" />
          Peças produzidas
        </h3>
      )}

      <div className="space-y-3">
        {aguardando.map(d => (
          <div key={d.id} className="bg-panel border border-pink/30 bg-pink/5 rounded-xl p-4">
            <h3 className="font-bold text-text">{d.titulo}</h3>
            {d.pauta && <p className="text-xs text-text-2 mt-1">{d.pauta}</p>}
            {d.linkFinal && (
              <a href={d.linkFinal} target="_blank" rel="noreferrer" className="text-xs text-blue-light mt-2 inline-flex items-center gap-1 hover:underline">
                Ver peça <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={() => aprovar.mutate({ id: d.id })}
                disabled={aprovar.isPending}
                className="flex-1 bg-green hover:bg-green/90 text-white text-xs font-bold py-2 rounded-md inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                <ThumbsUp className="w-3.5 h-3.5" /> Aprovar
              </button>
              <button type="button" onClick={() => setRevOpen(d)}
                className="flex-1 bg-purple/10 border border-purple/30 text-purple text-xs font-bold py-2 rounded-md inline-flex items-center justify-center gap-1.5 hover:bg-purple/20">
                <MessageSquareWarning className="w-3.5 h-3.5" /> Pedir ajustes
              </button>
            </div>
          </div>
        ))}
      </div>

      {revOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold">Pedir ajustes</h3>
                <p className="text-[11px] text-gray-500 line-clamp-1">{revOpen.titulo}</p>
              </div>
              <button onClick={() => setRevOpen(null)} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4">
              <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={5} autoFocus
                placeholder="Conta o que precisa ajustar…"
                className="w-full text-sm rounded-md border border-gray-300 bg-gray-50 p-2.5 focus:bg-white focus:border-blue-400 outline-none" />
            </div>
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button onClick={() => setRevOpen(null)} className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 rounded">Cancelar</button>
              <button onClick={() => feedback.trim() && revisao.mutate({ id: revOpen.id, feedback: feedback.trim() }, { onSuccess: () => { setRevOpen(null); setFeedback(''); } })}
                disabled={!feedback.trim() || revisao.isPending}
                className={clsx('px-4 py-2 text-xs font-bold rounded text-purple bg-purple/15 border border-purple/40 hover:bg-purple/25', (!feedback.trim() || revisao.isPending) && 'opacity-50 cursor-not-allowed')}>
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {revEscopoOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold">Pedir ajustes</h3>
                <p className="text-[11px] text-gray-500 line-clamp-1">{revEscopoOpen.entrega} · {revEscopoOpen.eventoSubtipo}</p>
              </div>
              <button onClick={() => setRevEscopoOpen(null)} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4">
              <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={5} autoFocus
                placeholder="Conta o que precisa ajustar…"
                className="w-full text-sm rounded-md border border-gray-300 bg-gray-50 p-2.5 focus:bg-white focus:border-blue-400 outline-none" />
            </div>
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button onClick={() => setRevEscopoOpen(null)} className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 rounded">Cancelar</button>
              <button onClick={() => feedback.trim() && revisaoEscopo.mutate({ id: revEscopoOpen.id, feedback: feedback.trim() }, { onSuccess: () => { setRevEscopoOpen(null); setFeedback(''); } })}
                disabled={!feedback.trim() || revisaoEscopo.isPending}
                className={clsx('px-4 py-2 text-xs font-bold rounded text-purple bg-purple/15 border border-purple/40 hover:bg-purple/25', (!feedback.trim() || revisaoEscopo.isPending) && 'opacity-50 cursor-not-allowed')}>
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
